import { Link } from 'react-router-dom';
import { ChatMulti, ChatMultiSummaryRow, MultiStatus, useChatMultis, useChatMultisRecord } from '../../queries/multis';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';

const STATUS_VARIANT: Record<MultiStatus, 'success' | 'destructive' | 'outline' | 'default'> = { won: 'success', lost: 'destructive', void: 'outline', pending: 'default' };
const STATUS_LABEL: Record<MultiStatus, string> = { won: 'Won', lost: 'Lost', void: 'Void', pending: 'Pending' };
/** "7.9%", or "1 in 160" once a percentage would read as 0 (as the Multis cards). */
const chanceText = (c: number) => (c >= 0.1 ? `${(c * 100).toFixed(1)}%` : `1 in ${Math.round(1 / c).toLocaleString('en-AU')}`);
const money = (v: number | null) => (v == null ? '-' : `$${Number(v).toFixed(2)}`);
const signed = (v: number | null) => (v == null ? '-' : `${v < 0 ? '-' : '+'}$${Math.abs(v).toFixed(2)}`);
const TIME = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', hour: 'numeric', minute: '2-digit' });

/** How the chat's multis went, by the price members asked for. */
function SummaryTable({ rows, label }: { rows: ChatMultiSummaryRow[]; label: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label={label}>
        <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="py-1 pr-2">Price asked</th>
            <th className="py-1 pr-2">Multis</th>
            <th className="py-1 pr-2">Asks</th>
            <th className="py-1 pr-2">Settled</th>
            <th className="py-1 pr-2">Won</th>
            <th className="py-1 pr-2">Expected</th>
            <th className="py-1">Profit ($1 each)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.band} className={`border-t border-border ${r.band === 'All' ? 'font-semibold' : ''}`}>
              <td className="py-1.5 pr-2">{r.band}</td>
              <td className="py-1.5 pr-2">{r.multis}</td>
              <td className="py-1.5 pr-2">{r.asks}</td>
              <td className="py-1.5 pr-2">{r.settled}</td>
              <td className="py-1.5 pr-2">{r.won}</td>
              <td className="py-1.5 pr-2">{r.expected.toFixed(1)}</td>
              <td className="py-1.5">{signed(r.profit)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChatMultiRow({ multi }: { multi: ChatMulti }) {
  return (
    <li className="space-y-1 rounded-md border border-border p-3" aria-label={`Chat multi ${multi.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">{TIME.format(new Date(multi.createdAt))}</span>
        <span>
          {multi.spin ? 'Spin another' : 'Asked'} for <b>{multi.askedPrice != null ? `$${multi.askedPrice}` : '-'}</b>, built <b>{money(multi.estPrice)}</b> ·{' '}
          {chanceText(multi.chance)} chance
        </span>
        <Link to={`/messages/${multi.conversationId}`} className="text-xs text-primary underline">
          Conversation
        </Link>
        <Badge variant={STATUS_VARIANT[multi.status]} className="ml-auto">
          {STATUS_LABEL[multi.status]}
          {multi.status === 'won' && multi.settledPrice != null && multi.settledPrice !== multi.estPrice ? ` (after voids ${money(multi.settledPrice)})` : ''}
        </Badge>
      </div>
      <ul className="text-sm">
        {multi.legs.map((l, i) => (
          <li key={i} className="flex flex-wrap gap-2">
            <span className="text-muted-foreground">{l.trackName} R{l.raceNumber}</span>
            <span>
              #{l.tabNumber} {l.horseName} {l.finishWithin === 1 ? 'Win' : `Top ${l.finishWithin}`}
            </span>
            <span className={l.status === 'won' ? 'text-success' : l.status === 'lost' ? 'text-destructive' : 'text-muted-foreground'}>
              {STATUS_LABEL[l.status]}
              {l.position && l.status !== 'void' ? ` (${l.position})` : ''}
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}

/**
 * The multis the chat built for members at the price they asked for (Ask Tippa, from 31 Oct 2026), settled from
 * results, with the last 30 days' record by price band. The value pick of the day is the morning build's, above.
 */
export function ChatMultis({ date }: { date: string }) {
  const { data, isLoading, error } = useChatMultis(date);
  const { data: record = [] } = useChatMultisRecord(30);
  const multis = data?.multis ?? [];

  return (
    <Card aria-label="Chat multis">
      <CardHeader>
        <CardTitle className="text-base">Chat multis</CardTitle>
        <p className="text-sm text-muted-foreground">
          Multis the chat built for members, at the price they asked for, on the prices at that moment. Asks leave out "spin another". Expected =
          the model's winners among the settled multis. Profit is at our estimated prices, not bookmaker quotes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {record.length > 0 && (
          <div className="space-y-1">
            <h3 className="text-sm font-semibold">Last 30 days</h3>
            <SummaryTable rows={record} label="Chat multis, last 30 days" />
          </div>
        )}
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <p className="text-sm text-destructive">Could not load the chat multis.</p>
        ) : multis.length === 0 ? (
          <p className="text-sm text-muted-foreground">The chat built no multis this day.</p>
        ) : (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">This day</h3>
            <SummaryTable rows={data!.summary} label="Chat multis, this day" />
            <ol className="space-y-2">
              {multis.map((m) => (
                <ChatMultiRow key={m.id} multi={m} />
              ))}
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
